import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Globe, ChevronDown } from 'lucide-react';
import logo from '../assests/favicon-logo.png';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸', name: 'English' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', name: 'Français' },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n: i18nInstance } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageDropdown]);

  // Prevent background scrolling when overlay is visible
  useEffect(() => {
    if (isChangingLanguage) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Disable scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Re-enable scrolling and restore position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to ensure styles are reset
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isChangingLanguage]);

  const handleLanguageChange = async (languageCode: string) => {
    if (languageCode === i18nInstance.language) {
      setShowLanguageDropdown(false);
      return;
    }
    
    setIsChangingLanguage(true);
    setShowLanguageDropdown(false);
    
    // Wait for 500ms animation then change language
    setTimeout(async () => {
      await i18n.changeLanguage(languageCode);
      setIsChangingLanguage(false);
    }, 500);
  };

  const currentLanguageInfo = LANGUAGES.find(lang => lang.code === i18nInstance.language);

  return (
    <>
      {/* Language change overlay */}
      {isChangingLanguage && (
        <div className="fixed inset-0 bg-white bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center flex flex-col items-center">
            <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mb-4">
              {/* <Globe className="w-8 h-8 text-indigo-600" /> */}
              <img src={logo} alt="CVMatch" className="w-20 h-20 rounded-full" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">CVMatch</h1>
            <div className="flex justify-center mt-4">
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce mx-1"></div>
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce mx-1 animation-delay-200"></div>
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce mx-1 animation-delay-400"></div>
            </div>
            <p className="text-indigo-600 text-lg font-semibold">
              {i18nInstance.language === 'fr' ? 'Changement de langue...' : 'Changing language...'}
            </p>
          </div>
        </div>
      )}
      
      {/* Language Switcher */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          disabled={isChangingLanguage}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-4 py-2 transition-all flex items-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title="Change language"
        >
          <Globe className="w-4 h-4 mr-2" />
          {currentLanguageInfo?.name || 'English'}
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showLanguageDropdown && (
          <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[140px] z-50 animate-slideIn">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isChangingLanguage}
                className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  i18nInstance.language === lang.code ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                }`}
              >
                <span className="mr-2">{lang.flag}</span>
                {lang.name}
                {i18nInstance.language === lang.code && (
                  <span className="ml-auto text-indigo-500">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
export default LanguageSwitcher; 